import { type FC, useCallback, useState } from 'react';

import { type Document } from '@contentful/rich-text-types';
import { Button } from '@vakantiesnl/components/src/atoms/Button';
import { Typography } from '@vakantiesnl/components/src/atoms/Typography';
import { ImageOverview } from '@vakantiesnl/components/src/molecules/ImageOverview';
import { PictureLightBox } from '@vakantiesnl/components/src/molecules/PictureLightBox';
import { RichText } from '@vakantiesnl/components/src/molecules/RichText';
import { type MicroCopy, type Nbc } from '@vakantiesnl/types';

import { useStyles } from './BrandedSearchFooter.style';

type Props = {
	title: string;
	description?: string;
	images?: (string | null | undefined)[];
	richText: Document | null;
	microCopies: MicroCopy;
	footerAnchor?: string;
};

export const BrandedSearchFooterComponent: FC<Props> = ({
	title,
	description,
	images,
	richText,
	microCopies,
	footerAnchor,
}) => {
	const { classes } = useStyles();
	const [isImageModalOpen, setIsImageModalOpen] = useState(false);
	const [selectedImageIndex, setSelectedImageIndex] = useState(0);

	const imageSources: Nbc.MappedBucketImgProps[] | undefined = images
		?.filter((imageUrl) => !!imageUrl)
		.map((imageUrl) => {
			return {
				url: imageUrl,
				rotationDegrees: undefined,
			} as Nbc.MappedBucketImgProps;
		});

	const onClick = useCallback(() => {
		if (document) {
			document.getElementById('search-results')?.scrollIntoView({
				behavior: 'smooth',
			});
		}
	}, []);

	const handleImageClick = useCallback(
		(index: number) => {
			if (imageSources && imageSources[index]) {
				/** Set the selected image index to open in the detail view */
				setSelectedImageIndex(index);
				setIsImageModalOpen(true);
			}
		},
		[imageSources],
	);

	const handleImageModalClose = useCallback(() => {
		setSelectedImageIndex(0);
		setIsImageModalOpen(false);
	}, []);

	return (
		<div className={classes.brandedSearchFooter}>
			<div>
				<Typography variant="headingLg" as="h2">
					{title}
				</Typography>
				{description?.length && (
					<Typography variant="bodyMd" as="p" className={classes.description}>
						{description}
					</Typography>
				)}
			</div>
			{!!imageSources && imageSources.length > 0 && (
				<div>
					<ImageOverview
						images={imageSources}
						imagesCount={imageSources.length - 1}
						onImageClick={handleImageClick}
						microCopies={microCopies}
					/>

					{!!isImageModalOpen && (
						<PictureLightBox
							title={title}
							open={isImageModalOpen}
							images={imageSources}
							startingIndex={selectedImageIndex}
							onClose={handleImageModalClose}
						/>
					)}
				</div>
			)}
			{richText && (
				<div className={classes.richTextContainer}>
					<RichText document={richText} />
				</div>
			)}
			{footerAnchor && (
				<div className={classes.footerButton}>
					<Button variant="primary" size="small" onClick={onClick} title={footerAnchor} />
				</div>
			)}
		</div>
	);
};
